import React, { useState } from 'react';
import { Star, Quote, Camera } from 'lucide-react';

const ReviewsGallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);

  const testimonials = [
    {
      text: 'What a fantastic way to see Uganda national park from the sky!',
      author: 'Trip Advisor',
      date: 'October 2019',
      rating: 5
    },
    {
      text: 'An incredible experience, one of the highlights of our trip.',
      author: 'Sarah M.',
      date: '2024',
      rating: 5
    },
    {
      text: '100% worth it! The sunrise view over the Mara was absolutely breathtaking.',
      author: 'John D.',
      date: '2024',
      rating: 5
    },
    {
      text: 'Something you can\'t get from a Jeep safari. The aerial perspective is magical.',
      author: 'Trip Advisor',
      date: 'October 2019',
      rating: 5
    },
    {
      text: 'Amazing flight! The champagne breakfast afterwards was very good and very surreal eating in the middle of the bush.',
      author: 'Emma R.',
      date: '2024',
      rating: 5
    },
    {
      text: 'Adventure of a lifetime. Wow, wow, wow! Sunrise in Uganda from a hot air balloon is unforgettable.',
      author: 'Michael K.',
      date: '2024',
      rating: 5
    }
  ];

  const gallery = [
    { url: 'https://images.unsplash.com/photo-1761976671459-8a27e6b78c09?w=800', caption: 'Soaring over Uganda' },
    { url: 'https://images.unsplash.com/photo-1516426122078-c23e76319801?w=800', caption: 'Sunrise over the savannah' },
    { url: 'https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?w=800', caption: 'Wildlife from above' },
    { url: 'https://images.unsplash.com/photo-1723270868972-fed4b06feae4?w=800', caption: 'Golden hour flight' },
    { url: 'https://images.unsplash.com/photo-1489392191049-fc10c97e64b6?w=800', caption: 'Balloon preparations' },
    { url: 'https://images.unsplash.com/photo-1516886635086-2b3c423c0947?w=800', caption: 'Champagne breakfast in the bush' }
  ];

  const renderStars = (rating) => {
    return [...Array(rating)].map((_, i) => (
      <Star key={i} className="w-5 h-5" style={{ fill: '#EE6A1F', color: '#EE6A1F' }} />
    ));
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        className="relative h-96 flex items-center justify-center text-white"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1761976671459-8a27e6b78c09?w=1200)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0" style={{ backgroundColor: 'rgba(101, 67, 33, 0.5)' }}></div>
        <div className="relative z-10 text-center px-4">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">Reviews & Gallery</h1>
          <p className="text-xl">Stories and moments from above</p>
        </div>
      </section>

      {/* Rating Overview */}
      <section className="py-12 px-4 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-gray-800 mb-4">"100% worth it!"</h2>
          <p className="text-xl text-gray-600 mb-6">Reviewed 2024</p>
          <div className="flex justify-center space-x-2 mb-4">
            {renderStars(5)}
          </div>
          <p className="text-gray-600">Rated 5.0 stars by our guests</p>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4 bg-stone-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-800 mb-12 text-center">What Our Guests Say</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((review, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow">
                <Quote className="w-10 h-10 mb-4" style={{ color: '#EE6A1F', opacity: 0.3 }} />
                <div className="flex space-x-1 mb-4">
                  {renderStars(review.rating)}
                </div>
                <p className="text-gray-700 italic mb-4">"{review.text}"</p>
                <div className="border-t border-gray-200 pt-4">
                  <p className="font-semibold text-gray-800">{review.author}</p>
                  <p className="text-sm text-gray-500">{review.date}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center mb-12">
            <Camera className="w-8 h-8 mr-3" style={{ color: '#EE6A1F' }} />
            <h2 className="text-3xl font-bold text-gray-800">Gallery</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {gallery.map((image, index) => (
              <div
                key={index}
                className="relative group cursor-pointer overflow-hidden rounded-xl shadow-md hover:shadow-xl transition-shadow"
                onClick={() => setSelectedImage(image)}
              >
                <img
                  src={image.url}
                  alt={image.caption}
                  className="w-full h-72 object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                  <p className="text-white p-4 font-medium">{image.caption}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-20 px-4 text-white" style={{ backgroundColor: '#EE6A1F' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-12">Voices from Above</h2>
          <div className="space-y-8">
            <blockquote className="text-2xl italic">"A journey for the soul"</blockquote>
            <blockquote className="text-2xl italic">"Definitely worth it"</blockquote>
            <blockquote className="text-2xl italic">"The experience that will stir your soul"</blockquote>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="max-w-5xl w-full">
            <img
              src={selectedImage.url}
              alt={selectedImage.caption}
              className="w-full h-auto rounded-lg"
            />
            <p className="text-white text-center mt-4 text-lg">{selectedImage.caption}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewsGallery;
